/**
 * Popup-side helpers for the shell <-> OAuth callback popup handshake.
 *
 * Companion to `packages/ui-deployed-shell/src/features/drive-oauth-popup.ts`.
 * See that file's protocol comment for the full message-type contract;
 * the string constants below MUST stay in sync.
 *
 * Why this exists: in storage-partitioned embed contexts (Chrome 3PCD,
 * Safari ITP, Firefox ETP) the shell iframe and the top-level
 * `/oauth/callback` popup read from DIFFERENT localStorage partitions
 * even though they share an origin. The runtime auth token the shell
 * seeded into `oauth-access-token` is invisible to the popup, so the
 * popup's `fetchOAuthCallbackIntegration` / `exchangeOAuthCallbackCode`
 * calls go out without a bearer header, the server returns 403 /
 * VISITOR, and the callback page surfaces "Failed to load integration
 * configuration." into ITS partition -- where the shell can't see the
 * completion marker either. Result: a perceived infinite retry loop.
 *
 * postMessage is window-to-window, partition-immune, and same-origin
 * between the popup and the shell iframe. We use it to (a) request
 * the runtime auth context from the shell on popup mount, and (b)
 * signal completion / error back to the shell just before
 * `window.close()`.
 *
 * A provider login page that responds with an enforcing
 * `Cross-Origin-Opener-Policy: same-origin` (Zendesk's sign-in and 2SV
 * pages, so a cold session) severs `window.opener` when the popup
 * navigates there, so the popup can neither ask for the auth context nor
 * report completion. A report-only policy (Google) does not sever it.
 * For popups opened from a top-level window of this origin, a same-origin
 * BroadcastChannel carries the same three messages, correlated by the
 * one-time code. It is never used from an embedded (iframe) opener:
 * storage partitioning puts the iframe and the popup in different
 * partitions, which is the bug the postMessage-only embed path fixed.
 */

import type { AuthConfig } from "@superblocksteam/shared";

// Relative on purpose: the app tree imports this module from outside the
// dashboard tsconfig project, so a `core/*` alias here does not resolve there.
import { uiLogger } from "../../../core/telemetry/logger";
import UIMetrics from "../../../core/telemetry/UIMetrics";

export const SB_OAUTH_CALLBACK_COMPLETE = "sb-oauth-callback-complete" as const;
export const SB_OAUTH_CALLBACK_READY = "sb-oauth-callback-ready" as const;
export const SB_OAUTH_CALLBACK_TOKEN = `sb-oauth-callback-${"token"}` as const;
/**
 * BroadcastChannel name prefix. Each attempt uses its own channel, named
 * after its one-time code, so a bystander on this origin cannot join a
 * well-known name and collect every attempt's traffic; it would need the
 * code, which lives only in the opener's memory and the popup's URL.
 */
export const SB_OAUTH_CALLBACK_CHANNEL_PREFIX = "sb-oauth-callback" as const;

export function oauthHandoffChannelName(oneTimeCode: string): string {
  return `${SB_OAUTH_CALLBACK_CHANNEL_PREFIX}-${oneTimeCode}`;
}

export type OAuthHandoffChannel = Pick<
  BroadcastChannel,
  "addEventListener" | "close" | "postMessage" | "removeEventListener"
>;

type CreateSameOriginOAuthHandoffChannelDeps = {
  isTopLevelWindow: () => boolean;
  open: (name: string) => OAuthHandoffChannel;
};

const defaultHandoffChannelDeps: CreateSameOriginOAuthHandoffChannelDeps = {
  isTopLevelWindow: () =>
    typeof window !== "undefined" && window.self === window.top,
  open: (name) => new BroadcastChannel(name),
};

/**
 * Open this attempt's same-origin handoff channel, or `undefined` when it
 * must not be used: from an embedded opener (see the module comment) or in a
 * browser without BroadcastChannel. Callers fall back to the opener-only
 * protocol; the unavailable case is logged because it degrades a COOP-severed
 * flow back to the old failure.
 */
export function createSameOriginOAuthHandoffChannel(
  oneTimeCode: string,
  deps: CreateSameOriginOAuthHandoffChannelDeps = defaultHandoffChannelDeps,
): OAuthHandoffChannel | undefined {
  if (!deps.isTopLevelWindow()) return undefined;
  try {
    return deps.open(oauthHandoffChannelName(oneTimeCode));
  } catch (error: unknown) {
    uiLogger.warn("OAuth handoff channel unavailable", {
      "oauth.error": error instanceof Error ? error.message : String(error),
    });
    UIMetrics.recordOAuthPopupAttemptEvent("channel_unavailable");
    return undefined;
  }
}

export type ShellTokenReply = {
  token: string;
  profileKey?: string;
  integrationId: string;
};

type OAuthCallbackContextReply = {
  authConfig?: AuthConfig;
  token?: string;
  codeVerifier?: string;
  profileKey?: string;
  integrationId: string;
};

type RequestTokenFromShellArgs = {
  oneTimeCode: string;
  /**
   * How long to wait for the shell's reply before rejecting. Embed
   * callback flows require this postMessage token; they must not fall
   * back to a storage partition that may be invisible to the opener.
   */
  timeoutMs: number;
};

type RequestTokenFromShellDeps = {
  /**
   * The `window.opener` reference, if any. `null` when the popup was
   * not opened by our shell or when COOP severed the opener relationship.
   */
  opener: Pick<Window, "postMessage"> | null;
  /**
   * Same-origin handoff channel, used in place of the opener when COOP
   * severed it. Ignored while an opener exists.
   */
  channel?: OAuthHandoffChannel;
  /** The popup's own window for attaching the `message` listener. */
  windowTarget: Pick<Window, "addEventListener" | "removeEventListener">;
  /**
   * Origin of this popup window. Incoming `SB_OAUTH_CALLBACK_TOKEN`
   * replies are accepted when `event.origin` matches this value or
   * `openerTargetOrigin` (cross-origin embed app opener).
   */
  popupOrigin: string;
  /**
   * Origin of `window.opener`. `SB_OAUTH_CALLBACK_READY` is posted
   * with this targetOrigin. Defaults to the popup origin when the
   * shell opened the popup; embed apps on another origin set
   * `openerOrigin` in OAuth state.
   */
  openerTargetOrigin: string;
  setTimeout: (cb: () => void, ms: number) => unknown;
  clearTimeout: (handle: unknown) => void;
};

/** postMessage targetOrigin for messages sent to `window.opener`. */
export function resolveOAuthOpenerTargetOrigin(
  openerOrigin: string | undefined,
  popupOrigin: string,
): string {
  return openerOrigin ?? popupOrigin;
}

/**
 * Ask the shell for the runtime auth token + profile key needed to
 * authenticate the callback page's same-origin API calls. Resolves
 * rejects if there is no opener, the request times out, or every
 * reply was rejected (wrong origin, mismatched oneTimeCode). Legacy
 * localStorage handoff never calls this helper.
 */
export function requestTokenFromShell(
  args: RequestTokenFromShellArgs,
  deps: RequestTokenFromShellDeps,
): Promise<ShellTokenReply> {
  return requestOAuthCallbackContextFromOpener(args, deps, "token").then(
    (reply) => {
      if (!reply.token) {
        throw new Error(
          "OAuth callback did not receive an authentication token from the opener.",
        );
      }
      return {
        token: reply.token,
        ...(reply.profileKey ? { profileKey: reply.profileKey } : undefined),
        integrationId: reply.integrationId,
      };
    },
  );
}

export function requestCodeVerifierFromOpener(
  args: RequestTokenFromShellArgs,
  deps: RequestTokenFromShellDeps,
): Promise<string> {
  return requestOAuthCallbackContextFromOpener(args, deps, "codeVerifier").then(
    (reply) => {
      if (!reply.codeVerifier) {
        throw new Error(
          "OAuth callback did not receive a code verifier from the opener.",
        );
      }
      return reply.codeVerifier;
    },
  );
}

export function requestAuthConfigFromOpener(
  args: RequestTokenFromShellArgs,
  deps: RequestTokenFromShellDeps,
): Promise<AuthConfig> {
  return requestOAuthCallbackContextFromOpener(args, deps, "authConfig").then(
    (reply) => {
      if (!reply.authConfig) {
        throw new Error(
          "OAuth callback did not receive an auth config from the opener.",
        );
      }
      return reply.authConfig;
    },
  );
}

export function requestOAuthContextFromOpener(
  args: RequestTokenFromShellArgs,
  deps: RequestTokenFromShellDeps,
): Promise<OAuthCallbackContextReply> {
  return requestOAuthCallbackContextFromOpener(args, deps, "oauthContext");
}

function readOAuthCallbackContextReply(
  data: unknown,
  oneTimeCode: string,
  requiredField: "authConfig" | "codeVerifier" | "oauthContext" | "token",
): OAuthCallbackContextReply | undefined {
  if (!data || typeof data !== "object") return undefined;
  if (!("type" in data) || !("oneTimeCode" in data)) return undefined;
  if (data.type !== SB_OAUTH_CALLBACK_TOKEN) return undefined;
  if (data.oneTimeCode !== oneTimeCode) return undefined;
  const token =
    "token" in data && typeof data.token === "string" && data.token
      ? data.token
      : undefined;
  const codeVerifier =
    "codeVerifier" in data &&
    typeof data.codeVerifier === "string" &&
    data.codeVerifier
      ? data.codeVerifier
      : undefined;
  const authConfig =
    "authConfig" in data &&
    data.authConfig &&
    typeof data.authConfig === "object"
      ? (data.authConfig as AuthConfig)
      : undefined;
  if (requiredField === "authConfig" && !authConfig) return undefined;
  if (requiredField === "token" && !token) return undefined;
  if (requiredField === "codeVerifier" && !codeVerifier) return undefined;
  if (requiredField === "oauthContext" && !authConfig && !codeVerifier) {
    return undefined;
  }
  const profileKey =
    "profileKey" in data && typeof data.profileKey === "string"
      ? data.profileKey
      : undefined;
  const integrationId =
    "integrationId" in data && typeof data.integrationId === "string"
      ? data.integrationId
      : "";
  return {
    ...(authConfig ? { authConfig } : undefined),
    ...(token ? { token } : undefined),
    ...(codeVerifier ? { codeVerifier } : undefined),
    ...(profileKey ? { profileKey } : undefined),
    integrationId,
  };
}

function requestOAuthCallbackContextFromOpener(
  args: RequestTokenFromShellArgs,
  deps: RequestTokenFromShellDeps,
  requiredField: "authConfig" | "codeVerifier" | "oauthContext" | "token",
): Promise<OAuthCallbackContextReply> {
  const opener = deps.opener;
  const channel = opener ? undefined : deps.channel;
  if (!opener && !channel) {
    return Promise.reject(new Error("OAuth callback popup has no opener."));
  }

  return new Promise<OAuthCallbackContextReply>((resolve, reject) => {
    let settled = false;
    let timeoutHandle: unknown = undefined;
    let messageHandler: ((evt: MessageEvent) => void) | undefined;

    const cleanup = () => {
      if (timeoutHandle !== undefined) {
        deps.clearTimeout(timeoutHandle);
        timeoutHandle = undefined;
      }
      if (messageHandler) {
        (channel ?? deps.windowTarget).removeEventListener(
          "message",
          messageHandler as EventListener,
        );
        messageHandler = undefined;
      }
    };

    const settle = (value: OAuthCallbackContextReply) => {
      if (settled) return;
      settled = true;
      cleanup();
      resolve(value);
    };

    const fail = (message: string) => {
      if (settled) return;
      settled = true;
      cleanup();
      reject(new Error(message));
    };

    const isTrustedTokenReplyOrigin = (origin: string) =>
      origin === deps.popupOrigin || origin === deps.openerTargetOrigin;

    messageHandler = (evt: MessageEvent) => {
      if (opener) {
        if (evt.source !== opener) return;
        if (!isTrustedTokenReplyOrigin(evt.origin)) return;
      }
      const reply = readOAuthCallbackContextReply(
        evt.data,
        args.oneTimeCode,
        requiredField,
      );
      if (reply) settle(reply);
    };
    (channel ?? deps.windowTarget).addEventListener(
      "message",
      messageHandler as EventListener,
    );

    timeoutHandle = deps.setTimeout(
      () =>
        fail(
          requiredField === "token"
            ? "OAuth callback did not receive an authentication token from the opener."
            : requiredField === "authConfig"
              ? "OAuth callback did not receive an auth config from the opener."
              : requiredField === "codeVerifier"
                ? "OAuth callback did not receive a code verifier from the opener."
                : "OAuth callback did not receive an auth config or code verifier from the opener.",
        ),
      args.timeoutMs,
    );

    // Posting AFTER the listener is attached prevents a synchronous
    // shell reply from firing before we'd see it.
    const ready = {
      type: SB_OAUTH_CALLBACK_READY,
      oneTimeCode: args.oneTimeCode,
    };
    if (opener) {
      opener.postMessage(ready, deps.openerTargetOrigin);
    } else {
      channel?.postMessage(ready);
    }
  });
}

type OAuthCallbackCompleteMessage = {
  type: typeof SB_OAUTH_CALLBACK_COMPLETE;
  oneTimeCode?: string;
  integrationId?: string;
  error?: string;
};

type OAuthCallbackReadyMessage = {
  type: typeof SB_OAUTH_CALLBACK_READY;
  oneTimeCode?: string;
};

function readOAuthCallbackMessage(
  value: unknown,
): OAuthCallbackCompleteMessage | OAuthCallbackReadyMessage | undefined {
  if (!value || typeof value !== "object" || !("type" in value)) {
    return undefined;
  }
  if (value.type === SB_OAUTH_CALLBACK_READY) {
    return {
      type: value.type,
      ...("oneTimeCode" in value && typeof value.oneTimeCode === "string"
        ? { oneTimeCode: value.oneTimeCode }
        : {}),
    };
  }
  if (value.type !== SB_OAUTH_CALLBACK_COMPLETE) {
    return undefined;
  }
  return {
    type: value.type,
    ...("oneTimeCode" in value && typeof value.oneTimeCode === "string"
      ? { oneTimeCode: value.oneTimeCode }
      : {}),
    ...("integrationId" in value && typeof value.integrationId === "string"
      ? { integrationId: value.integrationId }
      : {}),
    ...("error" in value && typeof value.error === "string"
      ? { error: value.error }
      : {}),
  };
}

/**
 * Tell the opener (shell iframe or builder modal window) that the
 * callback flow has finished. Posted before `window.close()` so the
 * host can settle partition-immune without localStorage handoff keys.
 * Without an opener the same-origin handoff channel carries it instead.
 *
 * No-op when there is neither (manually pasted OAuth URL).
 */
export function signalCompletionToShell(
  args: { oneTimeCode?: string; integrationId?: string; error?: string },
  deps: {
    opener: Pick<Window, "postMessage"> | null;
    channel?: OAuthHandoffChannel;
    targetOrigin: string;
  },
): void {
  if (!deps.opener && !deps.channel) return;
  if (!args.oneTimeCode && !args.integrationId) return;
  const payload: OAuthCallbackCompleteMessage = {
    type: SB_OAUTH_CALLBACK_COMPLETE,
  };
  if (args.oneTimeCode) payload.oneTimeCode = args.oneTimeCode;
  if (args.integrationId) payload.integrationId = args.integrationId;
  if (args.error) payload.error = args.error;
  if (deps.opener) {
    deps.opener.postMessage(payload, deps.targetOrigin);
  } else {
    deps.channel?.postMessage(payload);
  }
}

/** Returns true when `message` matches the pending OAuth attempt identity. */
export function oauthCallbackCompleteMessageMatches(
  message: OAuthCallbackCompleteMessage,
  pending: { oneTimeCode?: string; integrationId: string },
): boolean {
  if (message.oneTimeCode) {
    return message.oneTimeCode === pending.oneTimeCode;
  }
  if (message.integrationId) {
    return message.integrationId === pending.integrationId;
  }
  return false;
}

const OAUTH_POPUP_CLOSED_POLL_MS = 500;
/**
 * A COOP-severed popup reports `closed === true` to its opener while it is
 * still open on screen, which is indistinguishable from the user closing
 * it. When the same-origin channel exists, a closed popup therefore keeps
 * the attempt alive for this long, waiting for the callback page to reach
 * the channel; only then is the attempt reported as cancelled. Sized for a
 * cold provider sign-in with a second factor.
 */
export const OAUTH_SEVERED_POPUP_GRACE_MS = 5 * 60 * 1000;

/**
 * Attempts whose popup went dark and that are waiting on the channel. A new
 * attempt for the same integration from this document supersedes them so a
 * late outcome of an old attempt cannot land on a form that has since moved
 * on; attempts for other integrations on the page are left alone.
 */
export type SeveredOAuthAttempt = {
  integrationId: string;
  supersede: () => void;
};
const defaultSeveredAttempts = new Set<SeveredOAuthAttempt>();

type WaitForOAuthPopupCompletionArgs = {
  codeVerifier?: string;
  pending: { integrationId: string; oneTimeCode: string };
  openedWindow: Window | null | undefined;
  targetOrigin: string;
};

type OpenOAuthPopupAndWaitForCompletionArgs = {
  authConfig?: AuthConfig;
  codeVerifier?: string;
  pending: { integrationId: string; oneTimeCode: string };
  openPopup: () => Window | null | undefined;
  targetOrigin: string;
  /**
   * Called once when the popup has gone dark (closed, or severed by the
   * provider's COOP and still open) while the handoff channel can still
   * complete the attempt. The caller should stop showing the attempt as in
   * progress: the promise now settles only on a channel completion, or
   * silently as `[false, undefined]` when the grace period passes or a new
   * attempt supersedes this one.
   */
  onSeveredWait?: () => void;
};

type WaitForOAuthPopupCompletionDeps = {
  windowTarget?: Pick<Window, "addEventListener" | "removeEventListener">;
  setInterval?: (cb: () => void, ms: number) => unknown;
  clearInterval?: (handle: unknown) => void;
  setTimeout?: (cb: () => void, ms: number) => unknown;
  clearTimeout?: (handle: unknown) => void;
  /**
   * Opens the attempt's same-origin handoff channel for popups whose opener
   * COOP severs. Defaults to `createSameOriginOAuthHandoffChannel`, which
   * declines from an embedded window.
   */
  createHandoffChannel?: (
    oneTimeCode: string,
  ) => OAuthHandoffChannel | undefined;
  /** Registry of dark attempts a new attempt supersedes; injectable for tests. */
  severedAttempts?: Set<SeveredOAuthAttempt>;
};

/**
 * Opener-side waiter for a popup opened by this window (dynamic form connect,
 * builder modal). Resolves when the callback posts
 * `SB_OAUTH_CALLBACK_COMPLETE` or when the popup closes without a signal.
 */
export function waitForOAuthPopupCompletion(
  args: WaitForOAuthPopupCompletionArgs,
  deps: WaitForOAuthPopupCompletionDeps = {},
): Promise<[boolean, string | undefined]> {
  if (!args.openedWindow) {
    return Promise.resolve([
      false,
      "Unable to open the authentication window. Please check your browser's pop-up blocker settings and try again.",
    ]);
  }

  return openOAuthPopupAndWaitForCompletion(
    {
      codeVerifier: args.codeVerifier,
      openPopup: () => args.openedWindow,
      pending: args.pending,
      targetOrigin: args.targetOrigin,
    },
    deps,
  );
}

export function openOAuthPopupAndWaitForCompletion(
  args: OpenOAuthPopupAndWaitForCompletionArgs,
  deps: WaitForOAuthPopupCompletionDeps = {},
): Promise<[boolean, string | undefined]> {
  const windowTarget = deps.windowTarget ?? window;
  const setIntervalFn =
    deps.setInterval ?? ((cb, ms) => window.setInterval(cb, ms));
  const clearIntervalFn =
    deps.clearInterval ?? ((handle) => window.clearInterval(handle as number));
  const setTimeoutFn =
    deps.setTimeout ?? ((cb, ms) => window.setTimeout(cb, ms));
  const clearTimeoutFn =
    deps.clearTimeout ?? ((handle) => window.clearTimeout(handle as number));
  const createHandoffChannel =
    deps.createHandoffChannel ?? createSameOriginOAuthHandoffChannel;
  const severedAttempts = deps.severedAttempts ?? defaultSeveredAttempts;

  for (const attempt of [...severedAttempts]) {
    if (attempt.integrationId === args.pending.integrationId) {
      attempt.supersede();
    }
  }

  return new Promise<[boolean, string | undefined]>((resolve) => {
    let settled = false;
    let authConfig = args.authConfig;
    let codeVerifier = args.codeVerifier;
    let openedWindow: Window | null | undefined = undefined;
    // Correlation over the channel is by one-time code only, so an attempt
    // without one (implicit flows without OAuth state) gets no channel.
    let handoffChannel: OAuthHandoffChannel | undefined = args.pending
      .oneTimeCode
      ? createHandoffChannel(args.pending.oneTimeCode)
      : undefined;
    let pendingCompletion:
      | { message: OAuthCallbackCompleteMessage; source: Window }
      | undefined;
    let pendingReadySource: Window | undefined;
    let pollHandle: unknown;
    let popupClosedObservedAtLeastOnce = false;
    let severedGraceHandle: unknown;
    const severedAttempt: SeveredOAuthAttempt = {
      integrationId: args.pending.integrationId,
      supersede: () => {
        UIMetrics.recordOAuthPopupAttemptEvent("superseded", {
          result: "silent",
        });
        finish([false, undefined]);
      },
    };

    const finish = (result: [boolean, string | undefined]) => {
      if (settled) return;
      settled = true;
      codeVerifier = undefined;
      windowTarget.removeEventListener(
        "message",
        messageHandler as EventListener,
      );
      if (handoffChannel) {
        handoffChannel.removeEventListener(
          "message",
          channelMessageHandler as EventListener,
        );
        handoffChannel.close();
        handoffChannel = undefined;
      }
      if (pollHandle !== undefined) {
        clearIntervalFn(pollHandle);
        pollHandle = undefined;
      }
      if (severedGraceHandle !== undefined) {
        clearTimeoutFn(severedGraceHandle);
        severedGraceHandle = undefined;
      }
      severedAttempts.delete(severedAttempt);
      resolve(result);
    };

    const cancelledResult: [boolean, string] = [
      false,
      "Authentication was cancelled or encountered an error. Please try again.",
    ];

    // (Re)start the wait for a dark popup to reach the channel. Restarted on
    // READY so a slow exchange after a slow sign-in still fits. Expiry is
    // silent: the caller was released when the popup went dark, and a
    // popup the user really closed has nothing to report.
    const restartSeveredGrace = () => {
      if (severedGraceHandle !== undefined) clearTimeoutFn(severedGraceHandle);
      severedGraceHandle = setTimeoutFn(() => {
        UIMetrics.recordOAuthPopupAttemptEvent("grace_expired", {
          result: "silent",
        });
        finish([false, undefined]);
      }, OAUTH_SEVERED_POPUP_GRACE_MS);
    };

    const enterSeveredWait = () => {
      UIMetrics.recordOAuthPopupAttemptEvent("severed_wait");
      restartSeveredGrace();
      severedAttempts.add(severedAttempt);
      args.onSeveredWait?.();
    };

    const formatError = (error: string) =>
      error.startsWith("Authentication failed:")
        ? error
        : `Authentication failed: ${error}`;

    const warnCodeVerifierDrop = (
      dropReason: "identity_mismatch" | "source_mismatch",
    ) =>
      uiLogger.warn("OAuth code verifier reply dropped", {
        "oauth.drop_reason": dropReason,
        "oauth.integration_id": args.pending.integrationId,
      });

    const takeOAuthContextReply = () => {
      const reply = {
        ...(authConfig ? { authConfig } : undefined),
        type: SB_OAUTH_CALLBACK_TOKEN,
        oneTimeCode: args.pending.oneTimeCode,
        codeVerifier,
      };
      authConfig = undefined;
      codeVerifier = undefined;
      return reply;
    };

    const replyWithOAuthContext = (source: Window) => {
      if (!authConfig && !codeVerifier) return;
      if (source !== openedWindow) {
        warnCodeVerifierDrop("source_mismatch");
        return;
      }
      source.postMessage(takeOAuthContextReply(), args.targetOrigin);
    };

    const complete = (
      message: OAuthCallbackCompleteMessage,
      path: "opener" | "channel",
    ) => {
      if (!oauthCallbackCompleteMessageMatches(message, args.pending)) {
        // The popup finished a different attempt than the one this waiter is
        // holding. Nothing resolves, so the caller only ever sees the generic
        // popup-closed message a second later. Log it so a dropped completion
        // is distinguishable from a real cancel.
        uiLogger.warn("OAuth callback completion dropped", {
          "oauth.drop_reason": "identity_mismatch",
          "oauth.integration_id": args.pending.integrationId,
        });
        return;
      }
      UIMetrics.recordOAuthPopupAttemptEvent(
        path === "channel" ? "settled_channel" : "settled_opener",
        { result: message.error ? "error" : "success" },
      );
      if (message.error) {
        finish([false, formatError(message.error)]);
      } else {
        finish([true, ""]);
      }
    };

    const messageHandler = (evt: MessageEvent) => {
      if (evt.origin !== args.targetOrigin) return;
      const data = readOAuthCallbackMessage(evt.data);
      if (!data) return;
      if (
        data.type === SB_OAUTH_CALLBACK_READY &&
        (authConfig || codeVerifier)
      ) {
        if (data.oneTimeCode !== args.pending.oneTimeCode) {
          warnCodeVerifierDrop("identity_mismatch");
          return;
        }
        if (!isWindowMessageSource(evt.source)) {
          warnCodeVerifierDrop("source_mismatch");
          return;
        }
        if (!openedWindow) {
          pendingReadySource = evt.source;
          return;
        }
        replyWithOAuthContext(evt.source);
        return;
      }
      if (data.type !== SB_OAUTH_CALLBACK_COMPLETE) return;
      if (!openedWindow) {
        if (isWindowMessageSource(evt.source)) {
          pendingCompletion = { message: data, source: evt.source };
        }
        return;
      }
      if (evt.source !== openedWindow) {
        uiLogger.warn("OAuth callback completion dropped", {
          "oauth.drop_reason": "source_mismatch",
          "oauth.integration_id": args.pending.integrationId,
        });
        return;
      }
      complete(data, "opener");
    };

    // Same-origin channel traffic has no `source` window to check; the
    // one-time code is the only correlation, so it is required here even
    // though a postMessage completion may fall back to the integration id.
    const channelMessageHandler = (evt: MessageEvent) => {
      const data = readOAuthCallbackMessage(evt.data);
      if (!data || data.oneTimeCode !== args.pending.oneTimeCode) return;
      if (data.type === SB_OAUTH_CALLBACK_READY) {
        if (severedGraceHandle !== undefined) restartSeveredGrace();
        if (!authConfig && !codeVerifier) return;
        handoffChannel?.postMessage(takeOAuthContextReply());
        return;
      }
      complete(data, "channel");
    };

    windowTarget.addEventListener("message", messageHandler as EventListener);
    // Attached before the popup opens so the callback's READY cannot
    // precede the listener; BroadcastChannel does not buffer.
    handoffChannel?.addEventListener(
      "message",
      channelMessageHandler as EventListener,
    );

    openedWindow = args.openPopup();
    if (!openedWindow) {
      UIMetrics.recordOAuthPopupAttemptEvent("popup_blocked", {
        result: "error",
      });
      finish([
        false,
        "Unable to open the authentication window. Please check your browser's pop-up blocker settings and try again.",
      ]);
      return;
    }
    if (pendingCompletion?.source === openedWindow) {
      complete(pendingCompletion.message, "opener");
    }
    pendingCompletion = undefined;
    if (settled) return;
    if (pendingReadySource) {
      replyWithOAuthContext(pendingReadySource);
      pendingReadySource = undefined;
    }

    pollHandle = setIntervalFn(() => {
      if (!openedWindow?.closed) {
        popupClosedObservedAtLeastOnce = false;
        return;
      }
      if (!popupClosedObservedAtLeastOnce) {
        popupClosedObservedAtLeastOnce = true;
        return;
      }
      if (handoffChannel) {
        // Closed, or severed by the provider's COOP and still open: the
        // channel is the only way to tell, so give it the grace period.
        if (severedGraceHandle === undefined) enterSeveredWait();
        return;
      }
      UIMetrics.recordOAuthPopupAttemptEvent("popup_closed", {
        result: "cancelled",
      });
      finish(cancelledResult);
    }, OAUTH_POPUP_CLOSED_POLL_MS);
  });
}

function isWindowMessageSource(
  source: MessageEventSource | null,
): source is Window {
  return source !== null && "window" in source && source.window === source;
}

/**
 * Report an authorization-code exchange failure back to the opener via
 * `notifyFinished` (postMessage when an opener exists).
 */
export function reportExchangeCodeFailure(
  message: string,
  deps: {
    notifyFinished: (error?: string) => void;
  },
): void {
  deps.notifyFinished(message);
}
